/**
 * Authenticated API Service
 * Wraps fetch() to automatically attach JWT Bearer token from localStorage.
 * All API calls to the Node.js backend should use this instead of raw fetch().
 */

import { API_URL, apiFetch } from '../config/api';

const TOKEN_KEY = 'utms_token';

/**
 * Get the stored JWT token
 */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Store JWT token
 */
export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Remove stored JWT token
 */
export function removeToken() {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Check if a token exists (does NOT validate it)
 */
export function hasToken() {
  return !!getToken();
}

/**
 * Authenticated fetch wrapper.
 * Automatically attaches Authorization: Bearer <token> header.
 * Handles 401 responses by clearing token and redirecting to login.
 *
 * @param {string} endpoint - API endpoint (e.g. '/drones'). Will be prefixed with API_URL.
 * @param {object} options  - Standard fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>}
 */
export async function authFetch(endpoint, options = {}) {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  return apiFetch(url, options);
}

/**
 * Convenience: GET request with auth
 */
export async function authGet(endpoint) {
  return authFetch(endpoint, { method: 'GET' });
}

/**
 * Convenience: POST request with auth
 */
export async function authPost(endpoint, data) {
  return authFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Convenience: PUT request with auth
 */
export async function authPut(endpoint, data) {
  return authFetch(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Convenience: DELETE request with auth
 */
export async function authDelete(endpoint) {
  return authFetch(endpoint, { method: 'DELETE' });
}

// ============ AUTH-SPECIFIC API CALLS ============

/**
 * Login with email and password
 * @returns {{ success: boolean, token?: string, user?: object, message?: string }}
 */
export async function loginApi(email, password) {
  const res = await fetch(`${API_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  return res.json();
}

/**
 * Register a new user (operator by default, admin determined by ADMIN_EMAIL env var)
 * @returns {{ success: boolean, token?: string, user?: object, message?: string }}
 */
export async function registerApi(name, email, password) {
  const res = await fetch(`${API_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, password }),
  });
  return res.json();
}

/**
 * Get current user profile (requires auth)
 */
export async function getMeApi() {
  const res = await authFetch('/auth/me');
  if (!res.ok) return { success: false };
  return res.json();
}

/**
 * Update profile
 */
export async function updateProfileApi(data) {
  const res = await authPut('/auth/me', data);
  return res.json();
}

/**
 * Change password
 */
export async function changePasswordApi(currentPassword, newPassword) {
  const res = await authPut('/auth/password', { currentPassword, newPassword });
  return res.json();
}

/**
 * Upload profile photo
 * @param {File} file - The image file to upload
 */
export async function uploadProfilePhotoApi(file) {
  const token = getToken();
  const formData = new FormData();
  formData.append('photo', file);

  const res = await fetch(`${API_URL}/auth/upload-photo`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });
  return res.json();
}

/**
 * Remove profile photo
 */
export async function removeProfilePhotoApi() {
  const res = await authDelete('/auth/photo');
  return res.json();
}

/**
 * Logout — clears httpOnly cookie on server
 */
export async function logoutApi() {
  try {
    await authPost('/auth/logout');
  } catch (_) { /* best-effort */ }
}

// ============ ADMIN API CALLS ============

/** Get system stats (admin only) */
export async function getAdminStatsApi() {
  const res = await authGet('/admin/stats');
  return res.json();
}

/** Get all users (admin only) */
export async function getAdminUsersApi() {
  const res = await authGet('/admin/users');
  return res.json();
}

/** Update user role (admin only) */
export async function updateUserRoleApi(userId, role) {
  const res = await authPut(`/admin/users/${userId}/role`, { role });
  return res.json();
}

/** Create a user (admin only). Returns a one-time set-password token, valid 72 h. */
export async function inviteUserApi(name, email, role) {
  const res = await authPost('/auth/invite', { name, email, role });
  return res.json();
}

/** Email a member a fresh one-time set-password link (admin only). The link is never returned. */
export async function sendUserEmailApi(userId, type = 'reset') {
  const res = await authPost(`/admin/users/${userId}/send-email`, { type });
  return res.json();
}

/** Set a password from an invite/reset link token (public, no session) */
export async function resetPasswordApi(token, password) {
  const res = await fetch(`${API_URL}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });
  return res.json();
}

/** Request a password reset email (public) */
export async function forgotPasswordApi(email) {
  const res = await fetch(`${API_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return res.json();
}

/** Suspend (true) or reactivate (false) a user — replaces delete; data is kept (admin only) */
export async function setUserSuspendedApi(userId, suspended) {
  const res = await authPut(`/admin/users/${userId}/suspend`, { suspended });
  return res.json();
}

/** Permanently delete a user (admin only) */
export async function deleteUserApi(userId, moveTo) {
  const res = await authDelete(`/admin/users/${userId}${moveTo ? `?moveTo=${encodeURIComponent(moveTo)}` : ''}`);
  return res.json();
}

/** Give a drone and its saved missions to another account (admin only) */
export async function moveDroneApi(id, to) {
  const res = await authPost(`/admin/drones/${id}/move`, { to });
  return res.json();
}

/** Get all drones (admin only) */
export async function getAdminDronesApi() {
  const res = await authGet('/admin/drones');
  return res.json();
}

/** Get all missions (admin only) */
export async function getAdminMissionsApi() {
  const res = await authGet('/admin/missions');
  return res.json();
}


/** Live airspace reservations + positioned drones (admin only) */
export async function getAdminAirspaceApi() {
  const res = await authGet('/admin/airspace');
  return res.json();
}

/** Release a drone's live airspace reservation (admin only) */
export async function releaseIntentApi(droneId) {
  const res = await authPost(`/admin/airspace/${encodeURIComponent(droneId)}/release`, {});
  return res.json();
}

/** Audit trail (admin only) */
export async function getAdminAuditApi(limit = 100) {
  const res = await authGet(`/admin/audit?limit=${limit}`);
  return res.json();
}

/** Per-route API calls / errors / latency since backend boot (admin only) */
export async function getAdminMonitorApi() {
  const res = await authGet('/admin/monitor');
  return res.json();
}

/** All delivery jobs (admin only) */
export async function getAdminDeliveriesApi() {
  const res = await authGet('/admin/deliveries');
  return res.json();
}

/** Service health (public endpoint) */
export async function getHealthApi() {
  const res = await authGet('/health');
  return res.json();
}
